import { UserManager, User as OidcUser, UserManagerEvents } from 'oidc-client-ts';
import { oidcConfig, OIDC_ENABLED } from '../config/oidcConfig';
import { authLogger } from '../utils/logger';
import { getDynamicRedirectUri, getDynamicPostLogoutRedirectUri } from '../utils/authUtils';

// Backend API URL for token exchange (to bypass CORS issues with provider)
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || '';
const TOKEN_EXCHANGE_ENDPOINT = `${BACKEND_API_URL}/api/auth/oidc/token`;

/**
 * OIDC Service for renewmyday.com authentication
 * 
 * This service provides an alternative authentication mechanism
 * to the MSAL-based Azure AD authentication.
 * 
 * Note: Due to CORS limitations on the provider's token endpoint,
 * we use a backend proxy for token exchange.
 */
class OidcService {
  private userManager: UserManager | null = null;
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;
  private currentUser: OidcUser | null = null;

  constructor() {
    if (OIDC_ENABLED) {
      authLogger.debug('OIDC service instantiated (enabled)');
    } else {
      authLogger.debug('OIDC service instantiated (disabled by feature flag)');
    }
  }

  /**
   * Get or create the UserManager instance
   */
  private getUserManager(): UserManager {
    if (!this.userManager) {
      // Create config with current dynamic URIs
      const config = {
        ...oidcConfig,
        redirect_uri: getDynamicRedirectUri(),
        post_logout_redirect_uri: getDynamicPostLogoutRedirectUri(),
      };

      authLogger.debug('Creating OIDC UserManager with config:', {
        authority: config.authority,
        client_id: config.client_id,
        redirect_uri: config.redirect_uri,
        scope: config.scope,
      });

      this.userManager = new UserManager(config);
      this.setupEventHandlers();
    }
    return this.userManager;
  }

  /**
   * Setup event handlers for token lifecycle events
   */
  private setupEventHandlers(): void {
    if (!this.userManager) return;

    const events: UserManagerEvents = this.userManager.events;

    events.addUserLoaded((user: OidcUser) => {
      authLogger.debug('OIDC: User loaded', { 
        sub: user.profile.sub,
        email: user.profile.email 
      });
      this.currentUser = user;
      this.dispatchAuthEvent('oidcUserLoaded', { user });
    });

    events.addUserUnloaded(() => {
      authLogger.debug('OIDC: User unloaded');
      this.currentUser = null;
      this.dispatchAuthEvent('oidcUserUnloaded', {});
    });

    events.addAccessTokenExpiring(() => {
      authLogger.debug('OIDC: Access token expiring, will renew silently');
    });

    events.addAccessTokenExpired(() => {
      authLogger.warn('OIDC: Access token expired');
      this.dispatchAuthEvent('oidcTokenExpired', {});
    });

    events.addSilentRenewError((error: Error) => {
      authLogger.error('OIDC: Silent renew error:', error);
      this.dispatchAuthEvent('oidcSilentRenewError', { error });
    });

    events.addUserSignedOut(() => {
      authLogger.debug('OIDC: User signed out');
      this.currentUser = null;
      this.dispatchAuthEvent('oidcUserSignedOut', {});
    });
  }

  /**
   * Dispatch custom events for auth state changes
   */
  private dispatchAuthEvent(eventName: string, detail: any): void {
    const event = new CustomEvent(eventName, { detail });
    window.dispatchEvent(event);
  }

  /**
   * Initialize the OIDC service
   */
  async initialize(): Promise<void> {
    if (!OIDC_ENABLED) {
      authLogger.debug('OIDC initialization skipped (disabled)');
      return;
    }

    if (this.initializationPromise) {
      authLogger.debug('OIDC initialization already in progress, waiting...');
      return this.initializationPromise;
    }

    if (this.initialized) {
      authLogger.debug('OIDC already initialized');
      return;
    }

    authLogger.debug('Starting OIDC initialization...');
    this.initializationPromise = this.performInitialization();

    try {
      await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  private async performInitialization(): Promise<void> {
    try {
      const userManager = this.getUserManager();

      // Check for redirect callback
      if (this.isOidcCallback()) {
        authLogger.debug('OIDC: Processing redirect callback...');
        await this.handleRedirectCallback();
      } else {
        // Try to load existing user from storage
        const user = await userManager.getUser();
        if (user && !user.expired) {
          authLogger.debug('OIDC: Loaded existing user from storage', {
            sub: user.profile.sub,
            email: user.profile.email,
          });
          this.currentUser = user;
        } else if (user && user.expired) {
          authLogger.debug('OIDC: Existing user token expired, clearing...');
          await userManager.removeUser();
        }
      }

      this.initialized = true;
      window.__OIDC_INITIALIZED__ = true;
      
      authLogger.debug('OIDC initialization completed');
      this.dispatchAuthEvent('oidcInitialized', {});
    } catch (error) {
      authLogger.error('OIDC initialization failed:', error);
      throw error;
    }
  }

  /**
   * Check if current URL is an OIDC callback
   */
  private isOidcCallback(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));
    
    // Check for authorization code flow (code in query params)
    const hasCode = !!urlParams.get('code');
    const stateFromQuery = urlParams.get('state');
    const stateFromHash = hashParams.get('state');
    const state = stateFromQuery || stateFromHash;
    
    // Check for implicit flow (tokens in hash fragment)
    const hasAccessToken = !!hashParams.get('access_token');
    const hasIdToken = !!hashParams.get('id_token');
    
    if (!(hasCode || hasAccessToken || hasIdToken) || !state) {
      return false;
    }
    
    // Verify this specific state belongs to an OIDC flow (not MSAL).
    // oidc-client-ts stores state entries as `oidc.{state_value}` in sessionStorage.
    const hasMatchingOidcState = sessionStorage.getItem(`oidc.${state}`) !== null;
    
    return hasMatchingOidcState;
  }

  /**
   * Handle redirect callback after authentication
   * Uses backend proxy for token exchange to bypass CORS issues
   */
  async handleRedirectCallback(): Promise<OidcUser | null> {
    try {
      // First, try to extract the authorization code from the URL
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      if (code && state) {
        authLogger.debug('OIDC: Found authorization code, exchanging via backend proxy...');
        
        // Get stored PKCE code_verifier from session storage
        const storedState = this.getStoredOidcState(state);
        const codeVerifier = storedState?.code_verifier;
        
        // Exchange code for tokens via backend proxy
        const tokens = await this.exchangeCodeViaBackend(code, codeVerifier);
        
        if (tokens) {
          // Create user from tokens and store
          const user = await this.createUserFromTokens(tokens);
          this.currentUser = user;
          
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
          
          authLogger.debug('OIDC: Redirect callback processed successfully via backend', {
            sub: user.profile.sub,
            email: user.profile.email,
          });
          
          return user;
        }
      }

      // Fallback to standard oidc-client-ts callback (will fail due to CORS)
      authLogger.debug('OIDC: Attempting standard callback processing...');
      const userManager = this.getUserManager();
      const user = await userManager.signinRedirectCallback();
      
      authLogger.debug('OIDC: Redirect callback processed successfully', {
        sub: user.profile.sub,
        email: user.profile.email,
      });
      
      this.currentUser = user;
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      return user;
    } catch (error) {
      authLogger.error('OIDC: Failed to process redirect callback:', error);
      
      // Do NOT clean URL on error — other auth providers (MSAL) may still need
      // the code/state query parameters to complete their redirect flow.
      
      throw error;
    }
  }

  /**
   * Get stored OIDC state from session storage
   */
  private getStoredOidcState(state: string): { code_verifier?: string } | null {
    try {
      // oidc-client-ts stores state with prefix
      const stateKey = `oidc.${state}`;
      const storedData = sessionStorage.getItem(stateKey);
      
      if (storedData) {
        return JSON.parse(storedData);
      }
      
      // Try alternate key format
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.includes(state)) {
          const data = sessionStorage.getItem(key);
          if (data) {
            return JSON.parse(data);
          }
        }
      }
      
      return null;
    } catch (error) {
      authLogger.warn('OIDC: Failed to get stored state:', error);
      return null;
    }
  }

  /**
   * Exchange authorization code for tokens via backend proxy
   * This bypasses CORS issues with the provider's token endpoint
   */
  private async exchangeCodeViaBackend(code: string, codeVerifier?: string): Promise<any | null> {
    try {
      const redirectUri = getDynamicRedirectUri();
      
      authLogger.debug('OIDC: Exchanging code via backend proxy', { 
        redirectUri,
        hasCodeVerifier: !!codeVerifier 
      });

      const response = await fetch(TOKEN_EXCHANGE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          redirect_uri: redirectUri,
          code_verifier: codeVerifier,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        authLogger.error('OIDC: Backend token exchange failed:', {
          status: response.status,
          error: errorData,
        });
        throw new Error(`Token exchange failed: ${response.status}`);
      }

      const tokens = await response.json();
      authLogger.debug('OIDC: Backend token exchange successful');
      
      // DEBUG: Log token structure
      // console.log('=== OIDC TOKEN RESPONSE ===');
      // console.log('Full response:', tokens);
      // if (tokens.access_token) {
      //   console.log('Access token (decoded):', this.parseJwt(tokens.access_token));
      // }
      // if (tokens.id_token) {
      //   console.log('ID token (decoded):', this.parseJwt(tokens.id_token));
      // }
      // console.log('=== END TOKEN DEBUG ===');
      
      return tokens;
    } catch (error) {
      authLogger.error('OIDC: Failed to exchange code via backend:', error);
      return null;
    }
  }

  /**
   * Create an OidcUser-like object from token response
   */
  private async createUserFromTokens(tokens: any): Promise<OidcUser> {
    // Parse the ID token to get user profile
    const idToken = tokens.id_token;
    const accessToken = tokens.access_token;
    const expiresIn = tokens.expires_in || 3600;
    
    // Decode ID token payload (JWT)
    const profile = this.parseJwt(idToken);
    
    // Calculate expiration
    const expiresAt = Math.floor(Date.now() / 1000) + expiresIn;
    
    // Create user object compatible with oidc-client-ts
    const user: OidcUser = {
      id_token: idToken,
      access_token: accessToken,
      token_type: tokens.token_type || 'Bearer',
      scope: tokens.scope || 'openid profile email',
      profile: {
        sub: profile.sub || '',
        iss: profile.iss || '',
        aud: profile.aud || '',
        exp: profile.exp || expiresAt,
        iat: profile.iat || Math.floor(Date.now() / 1000),
        name: profile.name || '',
        email: profile.email || '',
        preferred_username: profile.preferred_username || profile.email || '',
        given_name: profile.given_name || '',
        family_name: profile.family_name || '',
      },
      expires_at: expiresAt,
      expired: false,
      scopes: (tokens.scope || 'openid profile email').split(' '),
      state: null,
      toStorageString: function() {
        return JSON.stringify(this);
      },
    } as OidcUser;

    // Store user in session storage
    const userManager = this.getUserManager();
    await userManager.storeUser(user);

    return user;
  }

  /**
   * Parse JWT token without verification (for extracting claims)
   */
  private parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      authLogger.error('OIDC: Failed to parse JWT:', error);
      return {};
    }
  }

  /**
   * Initiate login redirect
   */
  async signIn(): Promise<void> {
    if (!OIDC_ENABLED) {
      throw new Error('OIDC authentication is not enabled');
    }

    try {
      authLogger.debug('OIDC: Initiating sign-in redirect...');
      
      // Store auth provider preference
      sessionStorage.setItem('auth_provider', 'oidc');
      
      const userManager = this.getUserManager();
      await userManager.signinRedirect();
    } catch (error) {
      authLogger.error('OIDC: Sign-in failed:', error);
      throw error;
    }
  }

  /**
   * Sign out and redirect
   * 
   * Note: Since our OIDC provider may not have an end_session_endpoint configured
   * (to avoid CORS issues with discovery), we always clear local state first,
   * then attempt provider signout if the endpoint exists.
   */
  async signOut(): Promise<void> {
    try {
      authLogger.debug('OIDC: Initiating sign-out...');
      
      // 1. Always clear local state first to ensure clean logout
      this.currentUser = null;
      sessionStorage.removeItem('auth_provider');
      
      const userManager = this.getUserManager();
      
      // 2. Remove user from storage
      await userManager.removeUser();
      
      // 3. Clear any OIDC-related session storage
      await this.clearStorage();
      
      authLogger.debug('OIDC: Local state cleared successfully');
      
      // 4. Check if end_session_endpoint is configured before attempting redirect
      const hasEndSessionEndpoint = oidcConfig.metadata?.end_session_endpoint;
      
      if (hasEndSessionEndpoint) {
        authLogger.debug('OIDC: Attempting signout redirect to provider...');
        try {
          await userManager.signoutRedirect();
          // If redirect succeeds, we won't reach here
          return;
        } catch (redirectError) {
          authLogger.warn('OIDC: Provider signout redirect failed, using local logout only:', redirectError);
        }
      } else {
        authLogger.debug('OIDC: No end_session_endpoint configured, using local logout only');
      }
      
      // 5. Redirect to login page since we've cleared all local state
      window.location.href = '/login';
      
    } catch (error) {
      authLogger.error('OIDC: Sign-out failed:', error);
      
      // Ensure state is cleared even on error
      this.currentUser = null;
      sessionStorage.removeItem('auth_provider');
      
      // Best-effort storage cleanup
      try {
        await this.clearStorage();
      } catch (clearError) {
        authLogger.error('OIDC: Failed to clear storage during error recovery:', clearError);
      }
      
      // Redirect to login
      window.location.href = '/login';
    }
  }

  /**
   * Switch to a different account
   * 
   * Clears local state and redirects to login page with account selection.
   * For OIDC providers, we clear local state and redirect to login page
   * which allows the user to choose a different provider or account.
   */
  async switchAccount(): Promise<void> {
    if (!OIDC_ENABLED) {
      throw new Error('OIDC authentication is not enabled');
    }

    try {
      authLogger.debug('OIDC: Initiating account switch...');
      
      // 1. Clear local state
      this.currentUser = null;
      sessionStorage.removeItem('auth_provider');
      
      const userManager = this.getUserManager();
      
      // 2. Remove user from storage
      await userManager.removeUser();
      
      // 3. Clear any OIDC-related session storage
      await this.clearStorage();
      
      authLogger.debug('OIDC: Local state cleared for account switch');
      
      // 4. Redirect to login page with prompt for account selection
      // Using query param to signal the login page to show provider selection
      window.location.href = '/login?prompt=select_account';
      
    } catch (error) {
      authLogger.error('OIDC: Account switch failed:', error);
      
      // Ensure state is cleared even on error
      this.currentUser = null;
      sessionStorage.removeItem('auth_provider');
      
      // Best-effort storage cleanup
      try {
        await this.clearStorage();
      } catch (clearError) {
        authLogger.error('OIDC: Failed to clear storage during error recovery:', clearError);
      }
      
      // Redirect to login
      window.location.href = '/login?prompt=select_account';
    }
  }

  /**
   * Get current user
   */
  async getUser(): Promise<OidcUser | null> {
    if (!OIDC_ENABLED) return null;

    if (this.currentUser && !this.currentUser.expired) {
      return this.currentUser;
    }

    try {
      const userManager = this.getUserManager();
      const user = await userManager.getUser();
      
      if (user && !user.expired) {
        this.currentUser = user;
        return user;
      }
      
      return null;
    } catch (error) {
      authLogger.error('OIDC: Failed to get user:', error);
      return null;
    }
  }

  /**
   * Get ID token for API calls
   * Note: We use id_token instead of access_token because the backend
   * expects user claims (sub, email, etc.) that are in the id_token
   */
  async getAccessToken(): Promise<string | null> {
    const user = await this.getUser();
    
    if (!user) {
      authLogger.debug('OIDC: No user available for token');
      return null;
    }

    if (user.expired) {
      authLogger.debug('OIDC: Token expired, attempting silent renew...');
      try {
        const userManager = this.getUserManager();
        const renewedUser = await userManager.signinSilent();
        if (renewedUser) {
          this.currentUser = renewedUser;
          // Return id_token for backend authentication
          return renewedUser.id_token || renewedUser.access_token;
        }
      } catch (error) {
        authLogger.error('OIDC: Silent renew failed:', error);
        return null;
      }
    }

    // Return id_token for backend authentication (contains user claims)
    return user.id_token || user.access_token;
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getUser();
    return !!user && !user.expired;
  }

  /**
   * Check if OIDC is initialized
   */
  isInitialized(): boolean {
    return this.initialized || !!window.__OIDC_INITIALIZED__;
  }

  /**
   * Check if OIDC is enabled
   */
  isEnabled(): boolean {
    return OIDC_ENABLED;
  }

  /**
   * Clear all OIDC storage
   */
  async clearStorage(): Promise<void> {
    try {
      if (this.userManager) {
        await this.userManager.removeUser();
        await this.userManager.clearStaleState();
      }
      
      // Clear any OIDC-related session storage items
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.startsWith('oidc.') || key.startsWith('oidc:'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      this.currentUser = null;
      authLogger.debug('OIDC: Storage cleared');
    } catch (error) {
      authLogger.error('OIDC: Failed to clear storage:', error);
    }
  }

  /**
   * Get user profile info formatted similar to MSAL account
   */
  getUserProfile(): {
    localAccountId: string;
    homeAccountId: string;
    username: string;
    name: string;
    email: string;
  } | null {
    if (!this.currentUser) return null;

    const profile = this.currentUser.profile;
    
    return {
      localAccountId: profile.sub || '',
      homeAccountId: profile.sub || '',
      username: profile.email || profile.preferred_username || '',
      name: profile.name || profile.given_name || '',
      email: profile.email || '',
    };
  }
}

// Extend Window interface for OIDC state
declare global {
  interface Window {
    __OIDC_INITIALIZED__?: boolean;
  }
}

// Export singleton instance
export const oidcService = new OidcService();
